// FunnelDetail Page - Redesigned to match core analytics experience
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useSavedFunnels } from "@/hooks/useAnalytics";
import { parseSlugId } from "@/lib/routing/slug-id";
import { formatNumeric } from "@/lib/utils/stringUtils";
import { safeRatio } from "@/lib/utils/stats";
import { cn } from "@/lib/utils";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowLeft01Icon,
  Target01Icon,
  AnalyticsDownIcon,
  PencilEdit02Icon,
} from "@hugeicons/core-free-icons"
import { FeatureGate } from "@/components/shared/FeatureGate";

function FunnelDetailInner() {
  const params = useParams<{ funnelSlugId: string; domain?: string; workspaceId?: string; propertySlugId?: string; magicId?: string; password?: string }>();
  const { funnelSlugId } = params;
  const navigate = useNavigate();
  const ctx = useOutletContext<any>();
  const propertyId = ctx?.propertyId;
  const { data } = useSavedFunnels({ propertyId });


  // Extract funnel ID from slug
  const funnelId = funnelSlugId ? parseSlugId(funnelSlugId).id : null;

  // Find funnel from list
  const funnels = data?.funnels || [];
  const funnel = funnels.find(
    (f: any) => f.funnel_id === funnelId || f.funnelId === funnelId || f.id === funnelId
  );

  const handleBack = () => {
    navigate(-1);
  };

  if (!funnel) {
    return (
      <TooltipProvider>
        <div className="w-full">
          <div className="flex flex-col gap-6">
            <Button variant="ghost" onClick={handleBack} className="w-fit">
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} className="mr-2" />
              Back to Funnels
            </Button>
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <HugeiconsIcon icon={Target01Icon} size={48} className="text-muted-foreground/50 mb-4" />
                <p className="text-lg font-medium">Funnel not found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  This funnel doesn't exist or you don't have access to it
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  const funnelSteps = funnel.steps || [];
  const hasSteps = funnelSteps.length > 0;

  // Open this funnel in the Studio (where live analysis runs)
  const openInStudio = () => {
    const workspaceId = params.workspaceId;
    const propertySlugId = params.propertySlugId;
    const funnelQuery = funnelId ? `?funnel=${encodeURIComponent(funnelId)}` : "";
    if (workspaceId) {
      navigate(`/workspace/${workspaceId}/funnels${funnelQuery}`);
    } else if (propertySlugId) {
      navigate(`/p/${propertySlugId}/funnels${funnelQuery}`);
    } else {
      navigate(`/funnels${funnelQuery}`);
    }
  };

  if (!hasSteps) {
    return (
      <TooltipProvider>
        <div className="w-full">
          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={handleBack} className="text-muted-foreground">
                  ← Back
                </Button>
                <span className="text-sm font-semibold text-foreground">{funnel.name}</span>
              </div>
            </div>
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
                <HugeiconsIcon icon={Target01Icon} size={40} className="text-muted-foreground/40" />
                <p className="text-sm font-medium">No steps configured yet</p>
                <p className="text-xs text-muted-foreground text-center max-w-sm">
                  This funnel doesn't have any steps to display. Open it in the Studio to configure steps and run analysis.
                </p>
                <Button size="sm" onClick={openInStudio} className="mt-1">
                  <HugeiconsIcon icon={PencilEdit02Icon} size={14} className="mr-1.5" />
                  Open in Studio
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  const totalVisitors = funnelSteps[0]?.visitors || 0;
  const finalConversions = funnelSteps[funnelSteps.length - 1]?.visitors || 0;
  const overallConversionRate = totalVisitors > 0
    ? ((finalConversions / totalVisitors) * 100).toFixed(1)
    : "0";
  const totalDropoff = Math.max(0, totalVisitors - finalConversions);

  // Find highest dropoff step
  const highestDropoffStep = funnelSteps.reduce((max: any, step: any, idx: number) => {
    if (idx === 0) return max;
    return (step.dropoff > (max?.dropoff || 0)) ? { ...step, index: idx } : max;
  }, null);

  return (
    <TooltipProvider>
      <div className="w-full">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={handleBack} className="text-muted-foreground">
                ← Back
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{funnel.name}</span>
                <Badge variant="outline" className="text-[11px]">{funnelSteps.length} Steps</Badge>
              </div>
            </div>
          </div>

          {/* Overview Card: Metrics + Insights */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">Funnel overview</span>
                {funnel.description && (
                  <span className="text-[11px] text-muted-foreground">{funnel.description}</span>
                )}
              </div>

              {/* Metrics row - standard compact inline */}
              <div className="flex items-center gap-2 sm:gap-3 mb-4 pb-3 border-b border-border/20 overflow-x-auto scrollbar-hide">
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Visitors</span>
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {formatNumeric(totalVisitors)}
                  </span>
                </div>
                <div className="w-px h-3 bg-border/30 shrink-0" />
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Completed</span>
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {formatNumeric(finalConversions)}
                  </span>
                </div>
                <div className="w-px h-3 bg-border/30 shrink-0" />
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Conversion</span>
                  <span className="text-sm font-semibold text-primary tabular-nums">
                    {overallConversionRate}%
                  </span>
                </div>
                <div className="w-px h-3 bg-border/30 shrink-0" />
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Drop-off</span>
                  <span className="text-sm font-semibold text-primary-strong tabular-nums">
                    {formatNumeric(totalDropoff)}
                  </span>
                </div>
                <div className="w-px h-3 bg-border/30 shrink-0" />
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">Steps</span>
                  <span className="text-sm font-semibold text-foreground tabular-nums">
                    {funnelSteps.length}
                  </span>
                </div>
              </div>

              {/* Insights - compact */}
              <div className="flex items-center gap-3 flex-wrap">
                {highestDropoffStep && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <HugeiconsIcon icon={AnalyticsDownIcon} size={13} className="text-danger" />
                    <span className="text-muted-foreground">Highest dropoff:</span>
                    <span className="font-medium text-danger">
                      Step {highestDropoffStep.index + 1} ({highestDropoffStep.name}) at {Number.isFinite(highestDropoffStep.dropoff) ? highestDropoffStep.dropoff.toFixed(1) : "0"}%
                    </span>
                  </div>
                )}
                {parseFloat(overallConversionRate) >= 20 && (
                  <div className="flex items-center gap-1.5 text-xs">
                    <HugeiconsIcon icon={Target01Icon} size={13} className="text-success" />
                    <span className="text-muted-foreground">Above average conversion rate</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Step Breakdown - Data Table */}
          <Card className="data-table-card">
            <CardContent className="p-0">
              <div className="data-table-header">
                <h2 className="data-table-title">Step breakdown</h2>
              </div>

              <div className="responsive-table-wrapper">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b border-border/60 hover:bg-transparent">
                      <TableHead className="data-table-th min-w-[200px]">Step</TableHead>
                      <TableHead className="data-table-th text-right min-w-[90px]">Visitors</TableHead>
                      <TableHead className="data-table-th text-right min-w-[100px] hidden sm:table-cell">From previous</TableHead>
                      <TableHead className="data-table-th text-right min-w-[110px] hidden md:table-cell">Of total</TableHead>
                      <TableHead className="data-table-th text-right min-w-[90px]">Drop-off</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {funnelSteps.map((step: any, index: number) => {
                      const isLast = index === funnelSteps.length - 1;
                      const ofTotalPct = safeRatio(step.visitors, totalVisitors) * 100;
                      const fromPrevPct = index === 0 ? 100 : (Number.isFinite(step.conversionRate) ? step.conversionRate : 0);
                      const dropPct = index === 0 ? 0 : (Number.isFinite(step.dropoff) ? step.dropoff : 0);

                      return (
                        <TableRow key={index} className="data-table-row">
                          <TableCell className="data-table-cell data-table-cell-primary">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0",
                                index === 0 ? "bg-primary/10 text-primary" :
                                isLast ? "bg-success/10 text-success dark:text-success" :
                                "bg-muted text-muted-foreground"
                              )}>
                                {index + 1}
                              </span>
                              <div className="min-w-0">
                                <span className="truncate text-sm font-medium block">{step.name}</span>
                                <span className="text-[11px] text-muted-foreground font-mono truncate block">{step.url}</span>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="data-table-cell data-table-cell-numeric">
                            <span className="font-semibold">{formatNumeric(step.visitors)}</span>
                          </TableCell>
                          <TableCell className="data-table-cell data-table-cell-numeric hidden sm:table-cell">
                            {index === 0 ? (
                              <span className="text-muted-foreground">--</span>
                            ) : (
                              <span className={cn(
                                "font-medium",
                                fromPrevPct >= 50 ? "text-foreground" : "text-muted-foreground"
                              )}>
                                {fromPrevPct.toFixed(1)}%
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="data-table-cell data-table-cell-numeric hidden md:table-cell">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-chart-1/60 rounded-full"
                                  style={{ width: `${ofTotalPct}%` }}
                                />
                              </div>
                              <span className="font-medium tabular-nums">{ofTotalPct.toFixed(1)}%</span>
                            </div>
                          </TableCell>
                          <TableCell className="data-table-cell data-table-cell-numeric">
                            {index === 0 ? (
                              <span className="text-muted-foreground">--</span>
                            ) : (
                              <span className={cn(
                                "font-medium",
                                dropPct > 50 ? "text-primary-strong" : "text-muted-foreground"
                              )}>
                                {isLast && dropPct === 0 ? "0%" : `${dropPct.toFixed(1)}%`}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  );
}

export default function FunnelDetail() {
  return (
    <FeatureGate feature="funnels">
      <FunnelDetailInner />
    </FeatureGate>
  )
}
